import type {
  AdminReclaimDetail,
  AdminReclaimListItem,
} from "@/types/admin-reclaim";
import type { MockUserId } from "@/lib/api/mock-data/users.mock";

export const MOCK_RECLAIMS: Record<MockUserId, AdminReclaimDetail | null> = {
  u_001_alice_active: null,
  u_002_bob_trial_pending: null,
  u_003_cara_trial_ending: null,
  u_004_dave_card_failed: null,
  u_005_erin_charged_off: {
    id: 5,
    rmaId: "rma_005",
    userId: "u_005_erin_charged_off",
    serial: "ZB1-E005-23A5",
    trigger: "trial_return",
    status: "fee_charged",
    labelIssuedAt: "2026-03-17T10:00:00Z",
    lastScanAt: null,
    deadlineAt: "2026-03-31T23:59:59Z",
    carrier: "PostNord",
    trackingNumber: "PN9999000011FI",
    flags: ["deadline_passed", "no_carrier_scan"],
    refurbNotes: null,
    chargedOffAt: "2026-04-02T13:20:00Z",
    chargedOffAmountMinor: 84_000,
  },
  u_006_finn_past_due: null,
  u_007_gina_suspended: {
    id: 7,
    rmaId: "rma_007",
    userId: "u_007_gina_suspended",
    serial: "ZB1-G007-44CB",
    trigger: "fraud",
    status: "scrap",
    labelIssuedAt: null,
    lastScanAt: null,
    deadlineAt: null,
    carrier: null,
    trackingNumber: null,
    flags: ["fraud_hold", "device_blacklisted"],
    refurbNotes: "Device blacklisted at firmware level; scheduled for scrap.",
    chargedOffAt: null,
    chargedOffAmountMinor: null,
  },
  u_008_hugo_cancelled: null,
};

export const MOCK_RECLAIMS_BY_RMA: Record<string, AdminReclaimDetail> = {
  rma_005: MOCK_RECLAIMS.u_005_erin_charged_off!,
  rma_007: MOCK_RECLAIMS.u_007_gina_suspended!,
};

/**
 * Flat list rows for the reclaim queue. Backend will serve these directly
 * from GET /admin/reclaims; building the array here without per-user
 * fan-out keeps the mock client honest about what the real endpoint
 * returns.
 */
export const MOCK_RECLAIMS_LIST: AdminReclaimListItem[] = [
  {
    rmaId: "rma_005",
    userId: "u_005_erin_charged_off",
    serial: "ZB1-E005-23A5",
    trigger: "trial_return",
    status: "fee_charged",
    deadlineAt: "2026-03-31T23:59:59Z",
    lastScanAt: null,
    flags: ["deadline_passed", "no_carrier_scan"],
  },
  {
    rmaId: "rma_007",
    userId: "u_007_gina_suspended",
    serial: "ZB1-G007-44CB",
    trigger: "fraud",
    status: "scrap",
    deadlineAt: null,
    lastScanAt: null,
    flags: ["fraud_hold", "device_blacklisted"],
  },
  // Extra rows that exercise the carrier-pending / refurb-qc / refurb-passed
  // branches without being tied to any of the 8 canonical users.
  {
    rmaId: "rma_q12",
    userId: "u_006_finn_past_due",
    serial: "ZB1-F006-90DA",
    trigger: "warranty",
    status: "carrier_pending",
    deadlineAt: "2026-05-30T23:59:59Z",
    lastScanAt: null,
    flags: [],
  },
  {
    rmaId: "rma_q13",
    userId: "u_001_alice_active",
    serial: "ZB1-A001-77F1",
    trigger: "warranty",
    status: "refurb_qc_pending",
    deadlineAt: "2026-06-01T23:59:59Z",
    lastScanAt: "2026-05-15T11:00:00Z",
    flags: [],
  },
  {
    rmaId: "rma_q14",
    userId: "u_003_cara_trial_ending",
    serial: "ZB1-C003-19A0",
    trigger: "trial_return",
    status: "in_transit",
    deadlineAt: "2026-05-29T23:59:59Z",
    lastScanAt: "2026-05-19T15:22:00Z",
    flags: [],
  },
  // A damaged trial return whose $75 restocking fee was DECLINED. Real mode can
  // produce this (gateway sends feeStatus "failed"), so mock mode has to as well
  // — otherwise the "Damage fees declined" tile and the crit "Fee failed" badge
  // would be permanently 0 here and nobody would notice if they broke.
  {
    rmaId: "rma_q15",
    userId: "u_004_dave_card_failed",
    serial: "ZB1-D004-40BC",
    trigger: "trial_return",
    status: "refurb_damaged",
    deadlineAt: "2026-06-04T23:59:59Z",
    lastScanAt: "2026-05-21T09:10:00Z",
    flags: [],
    feeStatus: "failed",
  },
];
